import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Lock } from "react-feather";

import {
  DeletedOrg,
  DeletionStatus,
  DeletionTrigger,
} from "@safetyculture/s12-apis-connect-web/s12/accounts/history/v1/history_pb";

import mainStyles from "../../styles";
import { searchDeletedOrgs } from "api/endpoints/search";
import { callOptionsHeaders } from "api/helpers";
import {
  deletionStatusDisplayMapping,
  deletionTriggerDisplayMapping,
  subscriptionStatusDisplayMapping,
} from "constants/deleted-orgs";
import { DEFAULT_REGION_CODE } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

const DEFAULT_PAGE_SIZE = 20;

type DeletedOrgsFormState = {
  orgId: string;
  nameContains: string;
  emailDomain: string;
  deletionStatus: DeletionStatus;
  deletedTrigger: DeletionTrigger;
};

const emptyForm: DeletedOrgsFormState = {
  orgId: "",
  nameContains: "",
  emailDomain: "",
  deletionStatus: DeletionStatus.UNSPECIFIED,
  deletedTrigger: DeletionTrigger.UNSPECIFIED,
};

const formatTimestamp = (ts?: Timestamp) =>
  ts ? formatDateTimeDisplay(convertTimestampToDate(ts)) : "—";

const DeletedOrgsSearch = () => {
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);

  const [formState, setFormState] = useState<DeletedOrgsFormState>(emptyForm);
  // The query committed by the last Search click, so "See more" paginates the
  // same filters even if the form has since been edited.
  const [committedQuery, setCommittedQuery] =
    useState<DeletedOrgsFormState | null>(null);
  const [orgs, setOrgs] = useState<DeletedOrg[]>([]);
  const [nextPageToken, setNextPageToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<DeletedOrg | null>(null);

  const handleChange = (key: keyof DeletedOrgsFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const runSearch = (query: DeletedOrgsFormState, pageToken: string) => {
    setIsLoading(true);
    searchDeletedOrgs(
      {
        orgId: query.orgId,
        nameContains: query.nameContains,
        emailDomain: query.emailDomain,
        deletionStatus: query.deletionStatus,
        deletedTrigger: query.deletedTrigger,
        pageSize: DEFAULT_PAGE_SIZE,
        pageToken,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE),
    )
      .then((resp) => {
        setOrgs((prev) => (pageToken ? [...prev, ...resp.orgs] : resp.orgs));
        setNextPageToken(resp.nextPageToken);
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  const handleSearch = () => {
    // All criteria are optional — an empty search lists all deleted orgs.
    setOrgs([]);
    setNextPageToken("");
    setHasSearched(true);
    setCommittedQuery(formState);
    runSearch(formState, "");
  };

  const handleSeeMore = () => {
    if (committedQuery) {
      runSearch(committedQuery, nextPageToken);
    }
  };

  const handleClear = () => {
    setFormState(emptyForm);
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const deletionStatusChipStyleMap: Record<DeletionStatus, object> = {
    [DeletionStatus.UNSPECIFIED]: classes.neutral,
    [DeletionStatus.NOT_DELETED]: classes.blueMoon,
    [DeletionStatus.IN_PROGRESS]: classes.yellowSubmarine,
    [DeletionStatus.DONE]: classes.greenDay,
    [DeletionStatus.FAILED]: classes.redWine,
    [DeletionStatus.CANCELLED]: classes.orangeCrush,
  };

  const statusChip = (status: DeletionStatus) => (
    <Chip
      size="small"
      data-testid="deleted-org-status-chip"
      sx={{ ...deletionStatusChipStyleMap[status] }}
      label={deletionStatusDisplayMapping[status]}
    />
  );

  const triggerChip = (trigger: DeletionTrigger) => (
    <Chip
      size="small"
      variant="outlined"
      data-testid="deleted-org-trigger-chip"
      label={deletionTriggerDisplayMapping[trigger]}
    />
  );

  const shouldRenderSeeMore = !isLoading && orgs.length > 0 && !!nextPageToken;
  const shouldRenderCaughtUp = !isLoading && orgs.length > 0 && !nextPageToken;
  const shouldRenderEmpty = hasSearched && !isLoading && orgs.length === 0;

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Alert
          severity="info"
          icon={<Lock size={18} />}
          sx={{ mb: 2 }}
          data-testid="deleted-orgs-restricted-notice"
        >
          Deleted-org records are restricted and every search is audited.
        </Alert>

        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <TextField
            id="deleted-org-id-input"
            label="Org ID"
            value={formState.orgId}
            onChange={(event) => handleChange("orgId", event.target.value)}
            onKeyUp={handleKeyUp}
            variant="outlined"
            size="small"
            inputProps={{ "data-anchor": "deleted-org-id-input" }}
            sx={classes.textInput}
          />
          <TextField
            id="deleted-org-name-input"
            label="Org name"
            value={formState.nameContains}
            onChange={(event) =>
              handleChange("nameContains", event.target.value)
            }
            onKeyUp={handleKeyUp}
            variant="outlined"
            size="small"
            inputProps={{ "data-anchor": "deleted-org-name-input" }}
            sx={classes.textInput}
          />
          <TextField
            id="deleted-org-email-domain-input"
            label="Email domain"
            value={formState.emailDomain}
            onChange={(event) =>
              handleChange("emailDomain", event.target.value)
            }
            onKeyUp={handleKeyUp}
            variant="outlined"
            size="small"
            inputProps={{ "data-anchor": "deleted-org-email-domain-input" }}
            sx={classes.textInput}
          />
          <FormControl size="small" sx={classes.textInput}>
            <InputLabel id="deleted-org-status-label">Status</InputLabel>
            <Select
              labelId="deleted-org-status-label"
              label="Status"
              value={formState.deletionStatus}
              onChange={(event) => {
                setFormState((prev) => ({
                  ...prev,
                  deletionStatus: Number(event.target.value) as DeletionStatus,
                }));
              }}
              data-testid="deleted-org-status-select"
            >
              <MenuItem value={DeletionStatus.UNSPECIFIED}>Any status</MenuItem>
              <MenuItem value={DeletionStatus.NOT_DELETED}>
                Not deleted
              </MenuItem>
              <MenuItem value={DeletionStatus.IN_PROGRESS}>
                In progress
              </MenuItem>
              <MenuItem value={DeletionStatus.DONE}>Done</MenuItem>
              <MenuItem value={DeletionStatus.FAILED}>Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={classes.textInput}>
            <InputLabel id="deleted-org-trigger-label">Trigger</InputLabel>
            <Select
              labelId="deleted-org-trigger-label"
              label="Trigger"
              value={formState.deletedTrigger}
              onChange={(event) => {
                setFormState((prev) => ({
                  ...prev,
                  deletedTrigger: Number(event.target.value) as DeletionTrigger,
                }));
              }}
              data-testid="deleted-org-trigger-select"
            >
              <MenuItem value={DeletionTrigger.UNSPECIFIED}>
                Any trigger
              </MenuItem>
              <MenuItem value={DeletionTrigger.USER_INITIATED}>
                User initiated
              </MenuItem>
              <MenuItem value={DeletionTrigger.SUPPORT}>Support</MenuItem>
              <MenuItem value={DeletionTrigger.ABANDONED}>Abandoned</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            data-anchor="deleted-orgs-search-btn"
            data-testid="deleted-orgs-search-btn"
            onClick={handleSearch}
            disabled={isLoading}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="secondary"
            data-testid="deleted-orgs-clear-btn"
            onClick={handleClear}
          >
            Clear
          </Button>
        </Box>

        {orgs.length > 0 && (
          <Table size="small" data-testid="deleted-orgs-table">
            <TableHead>
              <TableRow>
                <TableCell>Org ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Owner email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Deleted at</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((org) => (
                <TableRow key={`${org.id}-${org.identifiedAt?.seconds}`} hover>
                  <TableCell>{org.id}</TableCell>
                  <TableCell>{org.name}</TableCell>
                  <TableCell>{org.email}</TableCell>
                  <TableCell>{statusChip(org.deletionStatus)}</TableCell>
                  <TableCell>{triggerChip(org.deletedTrigger)}</TableCell>
                  <TableCell>{formatTimestamp(org.deletedAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="primary"
                      data-testid="deleted-org-details-btn"
                      onClick={() => setSelectedOrg(org)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {isLoading && (
          <Box display="flex" justifyContent="center" sx={{ p: 2 }}>
            <CircularProgress size={28} data-testid="deleted-orgs-loading" />
          </Box>
        )}

        {shouldRenderEmpty && (
          <Typography
            variant="body2"
            sx={{ p: 2, color: "text.secondary" }}
            data-testid="deleted-orgs-empty"
          >
            No deleted orgs found for that search.
          </Typography>
        )}

        {(shouldRenderSeeMore || shouldRenderCaughtUp) && (
          <Box display="flex" justifyContent="flex-end" sx={{ p: 1 }}>
            {shouldRenderSeeMore && (
              <Button
                color="primary"
                size="small"
                onClick={handleSeeMore}
                data-testid="deleted-orgs-see-more-btn"
              >
                ...SEE MORE
              </Button>
            )}
            {shouldRenderCaughtUp && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                You&apos;re all caught up
              </Typography>
            )}
          </Box>
        )}
      </Grid>

      <DeletedOrgDetailDialog
        org={selectedOrg}
        onClose={() => setSelectedOrg(null)}
        statusChip={statusChip}
        triggerChip={triggerChip}
      />
    </Grid>
  );
};

type DeletedOrgDetailDialogProps = {
  org: DeletedOrg | null;
  onClose: () => void;
  statusChip: (status: DeletionStatus) => React.ReactNode;
  triggerChip: (trigger: DeletionTrigger) => React.ReactNode;
};

const DetailField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Grid>
);

const DeletedOrgDetailDialog = ({
  org,
  onClose,
  statusChip,
  triggerChip,
}: DeletedOrgDetailDialogProps) => {
  if (!org) {
    return null;
  }

  return (
    <Dialog
      open={!!org}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="deleted-org-detail-dialog"
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
      >
        {org.name || org.id}
        {statusChip(org.deletionStatus)}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="overline" sx={{ color: "text.secondary" }}>
          Deletion lifecycle
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2, mt: 0 }}>
          <DetailField
            label="Identified at"
            value={formatTimestamp(org.identifiedAt)}
          />
          <DetailField
            label="Current status"
            value={statusChip(org.deletionStatus)}
          />
          <DetailField
            label="Deleted at"
            value={formatTimestamp(org.deletedAt)}
          />
          <DetailField
            label="Triggered by"
            value={triggerChip(org.deletedTrigger)}
          />
          <DetailField label="Deleted by" value={org.deletedBy} />
        </Grid>
        <Divider />
        <Typography variant="overline" sx={{ color: "text.secondary" }}>
          Organisation
        </Typography>
        <Grid container spacing={1}>
          <DetailField label="Org ID" value={org.id} />
          <DetailField label="Region / cell" value={org.cell} />
          <DetailField label="Owner email" value={org.email} />
          <DetailField label="Legal email" value={org.legalEmail} />
          <DetailField label="URL" value={org.url} />
          <DetailField label="Plan" value={org.plan} />
          <DetailField
            label="Subscription status"
            value={subscriptionStatusDisplayMapping[org.subStatus]}
          />
          <DetailField label="Subscription ID" value={org.subId} />
          <DetailField
            label="Created at"
            value={formatTimestamp(org.createdAt)}
          />
          <DetailField
            label="Last activity"
            value={formatTimestamp(org.lastActivity)}
          />
          <DetailField label="Temporal ID" value={org.temporalId} />
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default DeletedOrgsSearch;
